import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import debounce from "lodash/debounce";
import apiClient from "./utils/apiClient.js";
import { playOrderSound } from "./utils/orderSound.js";
import "./App.css";
import logo from "./resources/cadmus_dark.svg";
import logoLight from "./resources/cadmus_light.svg";
import bg_ok from "./resources/bg_ok.png";
import bg_ok2x from "./resources/bg_ok@2x.png";
import bg_ok3x from "./resources/bg_ok@3x.png";
import bg_ok4x from "./resources/bg_ok@4x.png";
import bg_warning from "./resources/bg_warning.png";
import bg_warning2x from "./resources/bg_warning@2x.png";
import bg_warning3x from "./resources/bg_warning@3x.png";
import bg_warning4x from "./resources/bg_warning@4x.png";
import bg_critical from "./resources/bg_critical.png";
import bg_critical2x from "./resources/bg_critical@2x.png";
import bg_critical3x from "./resources/bg_critical@3x.png";
import bg_critical4x from "./resources/bg_critical@4x.png";
import backgroundVideo from "./resources/background.mp4";
import BrandCounter from "./components/BrandCounter";
import PortalActivity, { LoadingDots } from "./components/PortalActivity";
import onlineIcon from "./resources/icons/online.svg";
import offlineIcon from "./resources/icons/offline.svg";
import errorIcon from "./resources/icons/error.svg";
import collapseIcon from "./resources/icons/collapse.svg";
import expandIcon from "./resources/icons/expand.svg";
import ButtonGlass from "./components/ButtonGlass";
import managePortalsIcon from "./resources/icons/manage_portals.svg";
import sleepIcon from "./resources/icons/sleep.svg";
import DigitalClock from "./components/Clock";
import STATIC_PORTALS from "./components/portals";
import ManagePortals from "./components/ManagePortals";
import ReceivedComponent from "./components/ReceivedComponent";
import useElementPositionAndWindowSize from "./hooks/useElementPositionAndWindowSize";
import { useMobile } from "./hooks/useMobile";
import TransparentDataGrid from "./TransparentDataGrid.js";
import SimpleScoreboard from "./components/simpleScoreboard.js";
import { Fade } from '@mui/material'
import AnimatedScoreboard from "./components/animatedScoreboard.js";
import LoginScreen from "./components/LoginScreen.js";
import PolicyGate from "./components/PolicyGate.js";
import { clearSession } from "./utils/session.js";
import AvatarImage from "./components/AvatarImage.tsx";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LogoutIcon from '@mui/icons-material/Logout';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { RegisterProvider } from "./RegisterContext.js";
import RegisterScreen from "./Registerscreen.js";
import LabInfoScreen from "./Labinfoscreen.js";
import PricingScreen from "./Pricingscreen.js";
import AccountSettings from "./Accountsettings.jsx";
// ── Cycle timing constanten ────────────────────────────────────
const PORTAL_INTERVAL_MS = 3000;
const CYCLE_BUFFER_MS = 10000;
// Na een mislukte fetch ("critical") zo vaak opnieuw proberen
const CRITICAL_RETRY_MS = 15000;

// ── Sleep modus tijden (Amsterdam tijd) ──────────────────────
// Tussen WORK_START en WORK_END is de app in werkmodus.
// Buiten die tijden schakelt de app naar sleeping modus.
const WORK_START_HOUR = 7;   // 07:00
const WORK_END_HOUR = 21;  // productie
const WORK_END_MINUTE = 0;   // voor testen: bv 39 voor 21:39 — zet terug naar 0 voor productie

// Inline (i.p.v. <img src=account_settings.svg>): het menu wordt bij elk openen
// opnieuw opgebouwd en Safari liet een nieuwe <img> dan even leeg.
const AccountSettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 13 15" fill="none" aria-hidden="true" style={{ marginRight: 8, flexShrink: 0 }}>
    <path fill="#111111" d="M5.33333 8.66667V14H0C0 11.0545 2.38781 8.66667 5.33333 8.66667ZM5.33333 8C3.12333 8 1.33333 6.21 1.33333 4C1.33333 1.79 3.12333 0 5.33333 0C7.54333 0 9.33333 1.79 9.33333 4C9.33333 6.21 7.54333 8 5.33333 8ZM7.06307 11.8743C7.0218 11.7007 7 11.5196 7 11.3333C7 11.1471 7.0218 10.966 7.063 10.7924L6.40193 10.4107L7.0686 9.256L7.73013 9.63793C7.9922 9.39007 8.31173 9.20227 8.66667 9.0966V8.33333H10V9.0966C10.3549 9.20227 10.6744 9.39007 10.9365 9.63793L11.5981 9.25593L12.2648 10.4107L11.6037 10.7923C11.6449 10.966 11.6667 11.1471 11.6667 11.3333C11.6667 11.5195 11.6449 11.7007 11.6037 11.8743L12.2648 12.2559L11.5981 13.4107L10.9365 13.0287C10.6745 13.2766 10.355 13.4644 10.0001 13.57V14.3333H8.66673V13.5701C8.3118 13.4645 7.99227 13.2767 7.7302 13.0287L7.06867 13.4107L6.40193 12.256L7.06307 11.8743ZM9.33333 10.6667C8.96513 10.6667 8.66667 10.9651 8.66667 11.3333C8.66667 11.7015 8.96513 12 9.33333 12C9.70153 12 10 11.7015 10 11.3333C10 10.9651 9.70153 10.6667 9.33333 10.6667Z" />
  </svg>
);

// Geeft true als het nu werktijd is in Amsterdam
function isWorkTime() {
  const now = new Date();
  const amsterdamStr = now.toLocaleString("nl-NL", {
    timeZone: "Europe/Amsterdam", hour: "numeric", minute: "numeric", hour12: false
  });
  const [h, m] = amsterdamStr.split(":").map(Number);
  const totalMinutes = h * 60 + m;
  const startMinutes = WORK_START_HOUR * 60;
  const endMinutes = WORK_END_HOUR * 60 + WORK_END_MINUTE;
  return totalMinutes >= startMinutes && totalMinutes < endMinutes;
}

// Berekent hoeveel uren en minuten tot de volgende modus-switch
function getTimeUntilNextSwitch() {
  const now = new Date();
  const amsterdamStr = now.toLocaleString("nl-NL", {
    timeZone: "Europe/Amsterdam", hour: "numeric", minute: "numeric", hour12: false
  });
  const [h, m] = amsterdamStr.split(":").map(Number);
  const totalMinutes = h * 60 + m;
  const startMinutes = WORK_START_HOUR * 60;
  const endMinutes = WORK_END_HOUR * 60 + WORK_END_MINUTE;

  let diffMinutes;
  if (totalMinutes >= startMinutes && totalMinutes < endMinutes) {
    diffMinutes = endMinutes - totalMinutes;
  } else if (totalMinutes < startMinutes) {
    diffMinutes = startMinutes - totalMinutes;
  } else {
    diffMinutes = (24 * 60) - totalMinutes + startMinutes;
  }

  const hoursLeft = Math.floor(diffMinutes / 60);
  const minsLeft = diffMinutes % 60;
  const hoursPart = hoursLeft > 0 ? `${hoursLeft} hour${hoursLeft !== 1 ? "s" : ""}` : "";
  const minutesPart = minsLeft > 0 ? `${minsLeft} minute${minsLeft !== 1 ? "s" : ""}` : "";
  const separator = hoursPart && minutesPart ? " and " : "";
  return `${hoursPart}${separator}${minutesPart}` || "a moment";
}

// VideoBackground Component
function VideoBackground({ filtered, paused }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused]);
  return (
    <video ref={videoRef} autoPlay loop muted playsInline preload="auto"
      className={`background-video ${filtered ? "filtered" : ""}`}
    >
      <source src={backgroundVideo} type="video/mp4" />
      Your browser does not support this video.
    </video>
  );
}

// ProtectedRoute Component
// framer-motion (12.11) laat een element bij een hardware-versnelde (WAAPI) opacity-fade
// 1 frame op de beginwaarde staan zodra de fade klaar is (flikkering). Met een onUpdate
// prop valt framer terug op de JS-animatie: zelfde timing/easing, zonder die flits.
const noWaapi = () => {};

function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? <PolicyGate>{children}</PolicyGate> : <Navigate to="/login" replace />;
}

// ─────────────────────────────────────────────────────────────────────────────
// distributeDelta: verdeelt een totale delta over portals naar rato van hun
// totaal. Als de som door afrondingen niet klopt, corrigeer bij de grootste.
// ─────────────────────────────────────────────────────────────────────────────
function distributeDelta(portalsData, totalDelta) {
  if (totalDelta === 0 || portalsData.length === 0) {
    return portalsData.map(p => ({ ...p, assignedDelta: 0 }));
  }

  // Gebruik de echte per-portal delta als die beschikbaar is
  const realDeltas = portalsData.map(p => p.realDelta || 0);
  const realSum = realDeltas.reduce((s, d) => s + d, 0);

  let assigned;

  if (realSum === totalDelta) {
    // Echte deltas kloppen precies — gebruik die
    assigned = realDeltas;
  } else if (realSum > 0) {
    // Schaal de echte deltas naar het DB totaal
    assigned = realDeltas.map(d => Math.floor(d * (totalDelta / realSum)));
    const remainder = totalDelta - assigned.reduce((s, d) => s + d, 0);
    // Corrigeer remainder bij de portal met het grootste aandeel
    if (remainder !== 0) {
      const maxIdx = assigned.indexOf(Math.max(...assigned));
      assigned[maxIdx] += remainder;
    }
  } else {
    // Geen echte deltas — verdeeel naar rato van totaal per portal
    const grandTotal = portalsData.reduce((s, p) => s + (p.total_cases || 0), 0);
    if (grandTotal === 0) {
      assigned = portalsData.map(() => 0);
      assigned[0] += totalDelta; // alles bij de eerste
    } else {
      assigned = portalsData.map(p =>
        Math.floor((p.total_cases || 0) / grandTotal * totalDelta)
      );
      const remainder = totalDelta - assigned.reduce((s, d) => s + d, 0);
      if (remainder !== 0) {
        const maxIdx = portalsData
          .map((p, i) => ({ cases: p.total_cases || 0, i }))
          .sort((a, b) => b.cases - a.cases)[0].i;
        assigned[maxIdx] += remainder;
      }
    }
  }

  return portalsData.map((p, i) => ({ ...p, assignedDelta: assigned[i] }));
}

// DashboardScreen Component
function DashboardScreen({ onSearchModeChange, onDashboardVisibleChange, searchModeActive, demoVideoPaused, onDemoVideoPausedChange, demoHighlightPaused, onDemoHighlightPausedChange }) {
  const companyName = localStorage.getItem("company").endsWith(".")
    ? localStorage.getItem("company")
    : `${localStorage.getItem("company")}.`;
  const organizationId = localStorage.getItem("organization_id");
  const isMultiCompany = localStorage.getItem("multicompany") === "true";
  const userName = localStorage.getItem("username").endsWith(".")
    ? localStorage.getItem("username")
    : `${localStorage.getItem("username")}.`;

  const [dashboardState, setDashboardState] = useState("ok");
  // Presentation mode: warnings negeren (dashboard niet rood). "critical"
  // (data ophalen mislukt) blijft wel zichtbaar.
  const [demoWarningsHidden, setDemoWarningsHidden] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // ── Sleep modus ───────────────────────────────────────────────
  const [isSleeping, setIsSleeping] = useState(!isWorkTime());
  const [timeUntilSwitch, setTimeUntilSwitch] = useState(getTimeUntilNextSwitch());

  useEffect(() => {
    const checkSleepMode = () => {
      setIsSleeping(!isWorkTime());
      setTimeUntilSwitch(getTimeUntilNextSwitch());
    };
    checkSleepMode();

    // Sync met de klok — wacht tot het begin van de volgende minuut
    let interval = null;
    const msUntilNextMinute = (60 - new Date().getSeconds()) * 1000 - new Date().getMilliseconds();
    const timeout = setTimeout(() => {
      checkSleepMode();
      interval = setInterval(checkSleepMode, 60 * 1000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);

  // Effectieve dashboard state — sleeping overschrijft ok/warning maar niet critical
  const shownState = demoWarningsHidden && dashboardState === "warning" ? "ok" : dashboardState;
  const effectiveState = isSleeping && shownState !== "critical" ? "sleeping" : shownState;

  // ── Score: aparte state per tab ───────────────────────────────
  // Cache alleen gebruiken als het nog dezelfde dag is
  const todayStr = new Date().toDateString();
  const [scoreToday, setScoreToday] = useState(() => {
    const cachedDate = localStorage.getItem("scoreCache_date");
    const cachedScore = localStorage.getItem("scoreCache_today");
    return (cachedDate === todayStr && cachedScore) ? parseInt(cachedScore, 10) : 0;
  });
  const [scoreMonth, setScoreMonth] = useState(() => {
    const cached = localStorage.getItem("scoreCache_month");
    return cached ? parseInt(cached, 10) : 0;
  });
  const scoreTodayRef = useRef(scoreToday);
  const scoreMonthRef = useRef(scoreMonth);

  const [activeTab, setActiveTab] = useState("TODAY");
  const activeTabRef = useRef("TODAY");
  const isSwitchingTabRef = useRef(false);

  // score die het scoreboard ziet — bevroren tijdens tab-switch
  const score = activeTab === "TODAY" ? scoreToday : scoreMonth;

  const setScore = useCallback((valOrUpdater) => {
    const tab = activeTabRef.current;
    if (tab === "TODAY") {
      setScoreToday(prev => {
        const next = typeof valOrUpdater === "function" ? valOrUpdater(prev) : valOrUpdater;
        scoreTodayRef.current = next;
        localStorage.setItem("scoreCache_today", String(next));
        localStorage.setItem("scoreCache_date", new Date().toDateString());
        return next;
      });
    } else {
      setScoreMonth(prev => {
        const next = typeof valOrUpdater === "function" ? valOrUpdater(prev) : valOrUpdater;
        scoreMonthRef.current = next;
        localStorage.setItem("scoreCache_month", String(next));
        return next;
      });
    }
    // Tab-switch klaar zodra de eerste setScore binnenkomt
    isSwitchingTabRef.current = false;
  }, []);

  const [currentDate, setCurrentDate] = useState(new Date().toDateString());
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [highlightActive, setHighlightActive] = useState(true);
  const [highlightPosition, setHighlightPosition] = useState({ top: "80%", left: "50%" });
  const [isDashboardVisible, setIsDashboardVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldFadeIn, setShouldFadeIn] = useState(false);
  const toggleButtonRef = useRef(null);
  const [shouldFadeInToggle, setShouldFadeInToggle] = useState(false);
  const [counters, setCounters] = useState([]);
  const [portalActivityVisible, setPortalActivityVisible] = useState(true);
  const [startCycle, setStartCycle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recentCases, setRecentCases] = useState([]);
  const [isAnimationEnabled, setIsAnimationEnabled] = useState(true);

  // ── Logo reveal — zet op true om het neon logo te tonen ──────
  // Zet terug op false nadat de animatie klaar is (±3.6s)
  const [showLogo, setShowLogo] = useState(false);
  const logoTriggeredRef = useRef(false);
  useEffect(() => {
    if (!initialFetchDoneRef.current) return;
    if (logoTriggeredRef.current) return;
    if (scoreToday >= 1000) {
      logoTriggeredRef.current = true;
      setShowLogo(true);
      setTimeout(() => setShowLogo(false), 4000);
    }
  }, [scoreToday]);
  const dashboardContainerRef = useRef(null);
  const dashboardWrapperRef = useRef(null);
  const leftPanelRef = useRef(null);

  const [cycleTime, setCycleTime] = useState((10 * PORTAL_INTERVAL_MS) + CYCLE_BUFFER_MS);
  const cycleTimeRef = useRef(cycleTime);
  useEffect(() => { cycleTimeRef.current = cycleTime; }, [cycleTime]);

  const { position, windowSize } = useElementPositionAndWindowSize(leftPanelRef);
  const { isMobile, isPhone } = useMobile();
  const [dashboardRight, setDashboardRight] = useState(0);
  useEffect(() => {
    if (dashboardWrapperRef.current) {
      setDashboardRight(dashboardWrapperRef.current.getBoundingClientRect().right);
    }
  }, [windowSize]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isFilterMode, setIsFilterMode] = useState(false);

  const inboxButtonVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, delay: 1.2, ease: "easeOut" } },
    exit: { opacity: 0, y: -80, transition: { duration: 0.4, ease: "circIn", delay: 0 } },
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // ── debouncedSetScore: voor delta optellingen via portal animatie ──
  // Telt het assignedDelta van een portal op bij de actieve score.
  const debouncedSetScore = useCallback(
    debounce((newScore) => { setScore(newScore); }, 300),
    [setScore]
  );

  useEffect(() => { onSearchModeChange(isSearchMode); }, [isSearchMode, onSearchModeChange]);
  useEffect(() => { onDashboardVisibleChange(isDashboardVisible); }, [isDashboardVisible, onDashboardVisibleChange]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  useEffect(() => {
    const shouldShowGrid = sessionStorage.getItem('wasDashboardHidden') === 'true';
    if (shouldShowGrid) setIsDashboardVisible(false);
  }, []);

  useEffect(() => {
    apiClient.get("/dashboard")
      .then(r => console.log(r.data))
      // Geen redirect: een netwerkfout (bv. na slaapstand) is geen uitlog. Een
      // verlopen token (401) handelt de interceptor in apiClient al af.
      .catch(e => console.error(e));
  }, []);

  const dynamicKeyframes = `
    @keyframes moveHighlight {
      0%   { left:50%;top:80%;transform:scale(1) rotate(0deg);filter:blur(200px);opacity:0.6;box-shadow:0 0 40px rgba(169,50,173,0.4); }
      10%  { left:45%;top:65%;transform:scale(1.3) rotate(5deg);filter:blur(150px);opacity:0.8;box-shadow:0 0 60px rgba(169,50,173,0.6); }
      20%  { left:40%;top:70%;transform:scale(1) rotate(-5deg);filter:blur(180px);opacity:0.7;box-shadow:0 0 50px rgba(169,50,173,0.5); }
      25%  { left:0px;top:55%;transform:scale(1.3) rotate(0deg);filter:blur(100px);opacity:1;box-shadow:0 0 120px rgba(169,50,173,1.2); }
      30%  { left:0px;top:40%;transform:scale(1.3) rotate(5deg);filter:blur(120px);opacity:0.9;box-shadow:0 0 100px rgba(169,50,173,1); }
      85%  { left:0px;top:55%;transform:scale(1.3) rotate(-5deg);filter:blur(100px);opacity:1;box-shadow:0 0 120px rgba(169,50,173,1.2); }
      90%  { left:0px;top:60%;transform:scale(1.3) rotate(0deg);filter:blur(120px);opacity:0.9;box-shadow:0 0 100px rgba(169,50,173,1); }
      95%  { left:20%;top:65%;transform:scale(1.3) rotate(5deg);filter:blur(150px);opacity:0.8;box-shadow:0 0 60px rgba(169,50,173,0.6); }
      98%  { left:30%;top:80%;transform:scale(1) rotate(-5deg);filter:blur(180px);opacity:0.7;box-shadow:0 0 50px rgba(169,50,173,0.5); }
      100% { left:50%;top:80%;transform:scale(1) rotate(0deg);filter:blur(200px);opacity:0.6;box-shadow:0 0 40px rgba(169,50,173,0.4); }
    }
    @keyframes vibrate {
      0%,100% { transform:scale(1.3); }
      50%     { transform:scale(1.365); }
    }
  `;

  useEffect(() => {
    if (!isAnimationEnabled || demoHighlightPaused) setHighlightPosition({ top: "80%", left: "50%" });
  }, [isAnimationEnabled, demoHighlightPaused]);

  // ── Sleeping achtergrond — dot patroon op donkere achtergrond ──
  const [sleepingBgUrl, setSleepingBgUrl] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAZSURBVHgBlcAxDQAAAMKwBf+e4ZkBSoXCbfq9B/vVDkMEAAAAAElFTkSuQmCC";
    img.onload = () => {
      // Op de echte maat van het paneel (965×540) × pixeldichtheid, met
      // hele schermpixels per stip: voorheen 512×512 en dan uitgerekt, wat
      // de stippen wazig en ongelijk maakte
      const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1));
      const tileSize = 2 * dpr;
      const canvasW = 965 * dpr;
      const canvasH = 540 * dpr;
      const tile = document.createElement("canvas");
      tile.width = tileSize;
      tile.height = tileSize;
      const tctx = tile.getContext("2d");
      tctx.imageSmoothingEnabled = false;
      tctx.drawImage(img, 0, 0, tileSize, tileSize);
      const canvas = document.createElement("canvas");
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#111118";
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = ctx.createPattern(tile, "repeat");
      ctx.fillRect(0, 0, canvasW, canvasH);
      setSleepingBgUrl(canvas.toDataURL("image/png"));
    };
  }, []);

  const getBackground = () => {
    if (effectiveState === "sleeping") {
      const src = sleepingBgUrl || bg_ok;
      return { src, srcset: src };
    }
    // srcsetW (breedte-descriptors) voor Manage Portals: die toont dezelfde
    // achtergrond schermvullend, dus daar moet de browser op breedte kiezen
    // i.p.v. alleen op pixeldichtheid
    const widths = (files, base) => files.map((f, i) => `${f} ${base * (i + 1)}w`).join(", ");
    // Getoonde breedte bij cover: minstens schermbreed, breder als het scherm
    // verhoudingsgewijs hoger is dan de afbeelding (breedte/hoogte × 100vh)
    const coverSizes = (w, h) => `max(100vw, ${Math.ceil((w / h) * 100)}vh)`;
    const map = {
      ok: { src: bg_ok, srcset: `${bg_ok2x} 2x, ${bg_ok3x} 3x, ${bg_ok4x} 4x`, srcsetW: widths([bg_ok, bg_ok2x, bg_ok3x, bg_ok4x], 965), sizes: coverSizes(965, 540) },
      warning: { src: bg_warning, srcset: `${bg_warning2x} 2x, ${bg_warning3x} 3x, ${bg_warning4x} 4x`, srcsetW: widths([bg_warning, bg_warning2x, bg_warning3x, bg_warning4x], 965), sizes: coverSizes(965, 540) },
      critical: { src: bg_critical, srcset: `${bg_critical2x} 2x, ${bg_critical3x} 3x, ${bg_critical4x} 4x`, srcsetW: widths([bg_critical, bg_critical2x, bg_critical3x, bg_critical4x], 1456), sizes: coverSizes(1456, 540) },
    };
    return map[effectiveState] || map.ok;
  };

  const getStatusText = () => ({
    ok: "Cadmus is online",
    warning: "Some portals have issues – check status.",
    critical: "An error occurred",
    sleeping: "Cadmus is sleeping",
  }[effectiveState] || "Cadmus is online");

  const getStatusIcon = () => ({
    ok: onlineIcon, warning: offlineIcon, critical: errorIcon, sleeping: sleepIcon,
  }[effectiveState] || onlineIcon);

  const updateStatusBox = () => {
    const statusBox = document.querySelector(".status-box");
    if (!statusBox) return;
    const isOk = effectiveState === "ok";
    const isSleep = effectiveState === "sleeping";
    statusBox.style.background = isOk ? "#6c48d8" : isSleep ? "rgba(255,255,255,0.08)" : "#E91D51";
    statusBox.style.boxShadow = isOk
      ? "0px 0px 8px 4px rgba(108,72,216,0.6),0px 0px 16px 8px rgba(108,72,216,0.4),0px 0px 32px 16px rgba(108,72,216,0.2)"
      : isSleep ? "none"
        : "0px 4px 16px 0px rgba(233,29,81,0.60),0px 16px 64px 0px #E91D51";
    const kf = isOk
      ? `@keyframes pulsate{0%{box-shadow:0px 0px 8px 4px rgba(108,72,216,0.6),0px 0px 16px 8px rgba(108,72,216,0.4),0px 0px 32px 16px rgba(108,72,216,0.2)}50%{box-shadow:0px 0px 12px 6px rgba(108,72,216,0.8),0px 0px 24px 12px rgba(108,72,216,0.6),0px 0px 48px 24px rgba(108,72,216,0.4)}100%{box-shadow:0px 0px 8px 4px rgba(108,72,216,0.6),0px 0px 16px 8px rgba(108,72,216,0.4),0px 0px 32px 16px rgba(108,72,216,0.2)}}`
      : isSleep ? `@keyframes pulsate{0%{opacity:0.6}50%{opacity:0.3}100%{opacity:0.6}}`
        : `@keyframes pulsate{0%{box-shadow:0px 4px 16px 0px rgba(233,29,81,0.60),0px 16px 64px 0px #E91D51}50%{box-shadow:0px 8px 32px 0px rgba(233,29,81,0.80),0px 32px 128px 0px #E91D51}100%{box-shadow:0px 4px 16px 0px rgba(233,29,81,0.60),0px 16px 64px 0px #E91D51}}`;
    document.querySelector("#dynamic-keyframes")?.remove();
    const s = document.createElement("style");
    s.id = "dynamic-keyframes";
    s.innerHTML = kf;
    document.head.appendChild(s);
  };
  useEffect(() => { updateStatusBox(); }, [effectiveState]);

  const forceZeroDeltaRef = useRef(false);
  const pendingCountersRef = useRef(null);
  const pendingRecentCasesRef = useRef(null);

  // ── fetchDataRef — zodat handlePortalChange fetchData kan aanroepen ──
  // zonder "before initialization" error (handlePortalChange staat vóór fetchData)
  const fetchDataRef = useRef(null);
  // Timer voor de volgende fetch na een cycle; bewaard zodat hij bij
  // verlaten/terugkomen van het dashboard afgebroken kan worden
  const nextFetchTimerRef = useRef(null);
  const scheduleNextFetch = useCallback(() => {
    clearTimeout(nextFetchTimerRef.current);
    nextFetchTimerRef.current = setTimeout(() => {
      nextFetchTimerRef.current = null;
      fetchDataRef.current && fetchDataRef.current(false);
    }, CYCLE_BUFFER_MS);
  }, []);
  useEffect(() => () => clearTimeout(nextFetchTimerRef.current), []);

  // ── handlePortalChange ────────────────────────────────────────
  const handlePortalChange = useCallback((portal, newOrders, cycleCompleted) => {
    if (!isDashboardVisible) return;
    if (cycleCompleted) {
      // Negeer cycleCompleted als de initial fetch nog niet klaar is
      // — voorkomt dat een lopende cycle van de vorige tab de nieuwe tab verstoort
      if (!initialFetchDoneRef.current) return;
      // Cycle klaar — pending counters en recentCases nu toepassen
      if (pendingCountersRef.current) {
        setCounters(pendingCountersRef.current);
        pendingCountersRef.current = null;
      }
      if (pendingRecentCasesRef.current) {
        setRecentCases(pendingRecentCasesRef.current);
        pendingRecentCasesRef.current = null;
      }
      console.log(`[CYCLE] Klaar — volgende fetch over ${CYCLE_BUFFER_MS}ms`);
      scheduleNextFetch();
      return;
    }
    if (newOrders === 0) return;
    if (!initialFetchDoneRef.current) return;
    if (isSwitchingTabRef.current) return;
    if (["Admin", "SuperAdmin"].includes(localStorage.getItem("role"))) playOrderSound(newOrders);
    debouncedSetScore(prevScore => prevScore + newOrders);
  }, [isDashboardVisible, debouncedSetScore, scheduleNextFetch]);

  const navigate = useNavigate();
  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };
  const isAdmin = ["Admin", "SuperAdmin"].includes(localStorage.getItem("role"));

  // ── AbortController + deduping ────────────────────────────────
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isDashboardVisibleRef = useRef(true);
  useEffect(() => { isDashboardVisibleRef.current = isDashboardVisible; }, [isDashboardVisible]);

  // ── fetchData ─────────────────────────────────────────────────
  // isInitialFetch=true  → score uit DB, baseline reset, geen animatie
  // isInitialFetch=false → bereken deltas vooraf, verdeel over portals
  //                        zodat de score exact op de DB waarde uitkomt
  const fetchData = useCallback(async (isInitialFetch = false) => {
    if (!isDashboardVisibleRef.current || isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);

    try {
      const periodType = activeTabRef.current === "TODAY" ? "day" : "month";
      const storageKey = periodType === "day" ? "daycounters" : "monthcounters";

      if (isInitialFetch) {
        setSelectedPortals([]);
        // localStorage baseline NIET resetten hier — die wordt aan het einde
        // overschreven met de correcte data. Als we hier resetten en fetchData(false)
        // start voor deze fetch klaar is, berekent die het dagtotaal als delta.
      }

      // ── Haal cases per portal op ──────────────────────────────
      const response = await apiClient.get("/cases-count-per-portal", {
        params: { organizationId, period: periodType }, signal,
      });
      const casesData = Array.isArray(response.data) ? response.data : [];

      // ── Haal DB totaal score op ───────────────────────────────
      const scoreResponse = await apiClient.get("/cases-count-simple-scoreboard", {
        params: { organizationId, period: periodType }, signal,
      });
      const dbScore = (
        Array.isArray(scoreResponse.data) &&
        scoreResponse.data.length > 0 &&
        typeof scoreResponse.data[0].case_count === "number"
      ) ? scoreResponse.data[0].case_count : null;

      if (isInitialFetch && dbScore !== null) {
        console.log(`[FETCH] Initial — score=${dbScore}`);
        setScore(dbScore);
      }

      // ── Recent cases ──────────────────────────────────────────
      const recentResponse = await apiClient.get("/cases-recent", {
        params: { organizationId }, signal,
      });
      const recentCasesData = Array.isArray(recentResponse.data) ? recentResponse.data : [];

      // ── Portals ophalen ───────────────────────────────────────
      const portalsResponse = await apiClient.get("/configured-portals", {
        params: { organization_id: organizationId }, signal,
      });
      let configuredPortalsRaw = Array.isArray(portalsResponse.data) ? portalsResponse.data : [];
      if (configuredPortalsRaw.length > 0 && Array.isArray(configuredPortalsRaw[0])) {
        configuredPortalsRaw = configuredPortalsRaw[0];
      } else {
        throw new Error("Geen geldige portals data ontvangen");
      }

      const uniquePortalsMap = new Map();
      configuredPortalsRaw.forEach(p => {
        if (!uniquePortalsMap.has(p.portal_id)) uniquePortalsMap.set(p.portal_id, p);
      });
      configuredPortalsRaw.forEach(p => {
        const warnUser = p.warn_user === true || p.warn_user === 1 || p.warn_user === "1";
        const warnAdmin = p.warn_admin === true || p.warn_admin === 1 || p.warn_admin === "1";
        const existing = uniquePortalsMap.get(p.portal_id);
        if (existing) {
          uniquePortalsMap.set(p.portal_id, {
            ...existing, warn_user: existing.warn_user || warnUser, warn_admin: existing.warn_admin || warnAdmin,
          });
        }
      });

      const hasAnyWarning = Array.from(uniquePortalsMap.values()).some(p => p.warn_user);
      setDashboardState(hasAnyWarning ? "warning" : "ok");

      const configuredPortals = Array.from(uniquePortalsMap.values());
      const portalsMap = configuredPortals.reduce((acc, p) => {
        const sp = STATIC_PORTALS[p.portal_id] || {};
        acc[p.portal_id] = {
          ...sp,
          name: p.name || sp.name || "Unknown",
          id: p.portal_id,
          configuredId: p.id,
          status: p.status === 1 ? "active" : sp.status || "inactive",
        };
        return acc;
      }, {});

      const data = configuredPortals.map(p => {
        const cd = casesData.find(item => item.portal_id === p.portal_id) || {};
        return { portal_id: p.portal_id, total_cases: cd.total_cases || 0 };
      });

      // ── Scanners / counters ───────────────────────────────────
      const scannerCounts = casesData.reduce((acc, item) => {
        acc[item.portal_id] = item.total_cases || 0; return acc;
      }, {});
      const sortedScanners = Object.entries(scannerCounts)
        .map(([portalId, count]) => {
          const p = STATIC_PORTALS[portalId] || { display: "Unknown", logo: null };
          return { portal: p.display, count, logo: p.logo };
        })
        .sort((a, b) => b.count - a.count);
      while (sortedScanners.length < 3) sortedScanners.push({ portal: "Communicate", count: 0, logo: null });

      const newCycleTime = (sortedScanners.length * PORTAL_INTERVAL_MS) + CYCLE_BUFFER_MS;
      setCycleTime(newCycleTime);
      cycleTimeRef.current = newCycleTime;
      console.log(`[CYCLE] ${sortedScanners.length} portals → cycleTime=${newCycleTime}ms`);

      if (isInitialFetch) {
        setRecentCases(recentCasesData);
        setCounters(sortedScanners.slice(0, 3));
      } else {
        // Sla op in refs — wordt toegepast als cycle klaar is (cycleCompleted)
        // of bij score correctie
        pendingCountersRef.current = sortedScanners.slice(0, 3);
        pendingRecentCasesRef.current = recentCasesData;
      }

      // ── Delta berekening per portal ───────────────────────────
      let previousData = [];
      try {
        const stored = localStorage.getItem(storageKey);
        previousData = stored ? JSON.parse(stored) : [];
        if (!Array.isArray(previousData)) previousData = [];
      } catch { previousData = []; }

      const prevMap = new Map(
        previousData
          .filter(item => item && "portal_id" in item && "total_cases" in item)
          .map(item => [item.portal_id, item.total_cases || 0])
      );

      // Echte delta per portal — of geforceerd 0 na visibility correctie
      const forceZero = forceZeroDeltaRef.current;
      if (forceZero) forceZeroDeltaRef.current = false;

      const portalsWithRealDelta = data.map(item => ({
        ...item,
        realDelta: (isInitialFetch || forceZero) ? 0 : Math.max(0, (item.total_cases || 0) - (prevMap.get(item.portal_id) || 0)),
      }));

      // Nieuwe baseline opslaan
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch (e) { console.error(e); }

      if (!isInitialFetch && dbScore !== null) {
        const currentScore = activeTabRef.current === "TODAY" ? scoreTodayRef.current : scoreMonthRef.current;
        const totalDelta = dbScore - currentScore;

        // ── Drift check ───────────────────────────────────────────
        // Als het verschil groter is dan 5 is er waarschijnlijk drift
        // door een gemiste cycle of netwerkfout. Corrigeer direct naar
        // de DB waarde zonder animatie, portals tonen 0 new orders.
        if (Math.abs(totalDelta) > 5) {
          console.warn(`[DRIFT] currentScore=${currentScore}, dbScore=${dbScore}, delta=${totalDelta} → direct corrigeren`);
          setScore(dbScore);
          const updatedPortals = data.map(item => ({
            ...portalsMap[item.portal_id],
            Message: "0 new orders",
            ordersText: "0 new orders",
          }));
          setSelectedPortals(updatedPortals);
          setStartCycle(false);
          setPortalActivityVisible(true);
          setTimeout(() => { setStartCycle(true); }, 200);
          return; // sla de normale delta animatie over
        }

        console.log(`[DELTA] currentScore=${currentScore}, dbScore=${dbScore}, totalDelta=${totalDelta}`);

        // Verdeel de delta over portals — score landt exact op dbScore
        const distributed = distributeDelta(portalsWithRealDelta, totalDelta);

        const updatedPortals = distributed.map(item => ({
          ...portalsMap[item.portal_id],
          // Message wordt door PortalActivity geparsed als newOrders → score
          // We zetten assignedDelta hier zodat de score exact klopt
          Message: `${item.assignedDelta} new ${item.assignedDelta === 1 ? "order" : "orders"}`,
          // ordersText toont de echte delta aan de gebruiker
          ordersText: `${item.realDelta} new ${item.realDelta === 1 ? "order" : "orders"}`,
        }));

        setSelectedPortals(updatedPortals);
        setStartCycle(false);
        setPortalActivityVisible(true);
        setTimeout(() => { setStartCycle(true); }, 200);

      } else if (isInitialFetch) {
        // Initial: portals tonen met 0 new orders, cycle direct starten
        const updatedPortals = data.map(item => ({
          ...portalsMap[item.portal_id],
          Message: "0 new orders",
          ordersText: "0 new orders",
          assignedDelta: 0,
        }));
        setSelectedPortals(updatedPortals);
        setPortalActivityVisible(true);
        setStartCycle(false);
        setTimeout(() => { setStartCycle(true); }, 200);
      }

    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Fetch aborted");
        return;
      }
      console.error("Error fetching data:", error);
      setPortalActivityVisible(false);
      setDashboardState("critical");
      setScore(0);
      setCounters([]);
      setSelectedPortals([]);
      localStorage.setItem("daycounters", JSON.stringify([]));
      localStorage.setItem("monthcounters", JSON.stringify([]));
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [organizationId, setScore]);

  // fetchDataRef bijhouden zodat handlePortalChange altijd de laatste versie heeft
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, []);

  // ── handlePortalChange gebruikt assignedDelta uit het portal object ──
  // PortalActivity geeft newOrders mee vanuit portal.Message — maar wij
  // willen assignedDelta. We overschrijven handlePortalChange zodat hij
  // het juiste getal pakt.
  // Eigenlijk stuurt PortalActivity al de newOrders uit Message door.
  // Omdat wij Message instellen als realDelta maar assignedDelta anders
  // kan zijn, moeten we de score niet via handlePortalChange ophogen maar
  // via een aparte aanpak.
  //
  // Oplossing: we geven PortalActivity een portal object mee waarbij
  // Message het assignedDelta bevat (voor de score), en we tonen het
  // realDelta apart via een extra veld. Maar PortalActivity toont Message
  // als tekst — dus we willen "3 new orders" tonen (realDelta) maar
  // assignedDelta optellen bij de score.
  //
  // Aanpak: Message = realDelta voor de tekst weergave.
  // handlePortalChange pakt portal.assignedDelta voor de score.

  // ── checkAndResetForNewDay ────────────────────────────────────
  const checkAndResetForNewDay = useCallback(() => {
    const today = new Date().toDateString();
    if (activeTabRef.current === "TODAY" && today !== currentDate) {
      console.log(`New day detected: ${today}`);
      setCurrentDate(today);
      setScore(0);
      localStorage.setItem("daycounters", JSON.stringify([]));
      localStorage.removeItem("scoreCache_today"); // nieuwe dag = schone start
      if (isDashboardVisible) {
        fetchData(true);
      } else {
        setIsScoreLoading(true);
      }
    }
  }, [currentDate, isDashboardVisible, fetchData, setScore]);

  // ── Midnight reset ────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "TODAY") return;
    const interval = setInterval(() => { checkAndResetForNewDay(); }, 60 * 1000);
    checkAndResetForNewDay();
    return () => clearInterval(interval);
  }, [activeTab, checkAndResetForNewDay]);

  const initialFetchDoneRef = useRef(false);

  // ── Tab-switch / dashboard visible ────────────────────────────
  useEffect(() => {
    // Geplande fetch van een eerdere cycle afbreken — anders herstart die
    // de nieuwe cycle vroegtijdig na heen-en-weer naar de Inbox
    clearTimeout(nextFetchTimerRef.current);
    nextFetchTimerRef.current = null;
    if (isDashboardVisible) {
      // Cancel elke lopende fetch en score update van de vorige tab
      if (abortControllerRef.current) abortControllerRef.current.abort();
      isFetchingRef.current = false;
      initialFetchDoneRef.current = false;
      debouncedSetScore.cancel();
      isSwitchingTabRef.current = true;

      // Pending data van vorige tab weggooien
      pendingCountersRef.current = null;
      pendingRecentCasesRef.current = null;
      // Nieuwe tab score NIET resetten — laat huidige score staan
      // AnimatedScoreboard animeert pas als DB waarde binnenkomt via fetchData(true)
      forceZeroDeltaRef.current = true;

      // Baseline leegmaken
      localStorage.setItem("daycounters", JSON.stringify([]));
      localStorage.setItem("monthcounters", JSON.stringify([]));

      setPortalActivityVisible(false);
      setStartCycle(false);

      const t = setTimeout(async () => {
        await fetchData(true);
        initialFetchDoneRef.current = true;
        // fetchData(false) wordt getriggerd door PortalActivity cycleCompleted
        // fetchData(true) heeft de counters al direct gezet
      }, 100);
      return () => clearTimeout(t);
    }
  }, [activeTab, isDashboardVisible, fetchData]);

  // ── Herstel na "critical" ─────────────────────────────────────
  // Na een mislukte fetch (geen internet / backend weg) start er geen nieuwe
  // cycle meer. Daarom periodiek opnieuw proberen, en direct zodra de browser
  // weer online meldt. Een geslaagde fetch zet de state terug naar ok/warning,
  // waarna dit effect zichzelf opruimt.
  useEffect(() => {
    if (dashboardState !== "critical" || !isDashboardVisible) return;
    const retry = () => {
      if (document.visibilityState !== "visible") return;
      fetchDataRef.current && fetchDataRef.current(true);
    };
    const id = setInterval(retry, CRITICAL_RETRY_MS);
    window.addEventListener("online", retry);
    return () => {
      clearInterval(id);
      window.removeEventListener("online", retry);
    };
  }, [dashboardState, isDashboardVisible]);

  // ── Polling loop ──────────────────────────────────────────────
  // De volgende fetch wordt getriggerd door handlePortalChange(cycleCompleted=true)
  // vanuit PortalActivity. Geen interval of timer nodig.
  // Dit effect zorgt alleen voor de fadeIn animatie.
  useEffect(() => {
    if (!isDashboardVisible || isAnimating) return;
    const fadeTimeout = setTimeout(() => { setShouldFadeIn(true); }, 100);
    return () => clearTimeout(fadeTimeout);
  }, [isDashboardVisible, isAnimating]);

  // ── Visibility change ─────────────────────────────────────────
  // Score direct corrigeren, dan na CYCLE_BUFFER_MS nieuwe cycle starten.
  // PortalActivity hoeft geen eigen visibilitychange handler te hebben —
  // die reageert gewoon op de startCycle prop die fetchData(false) zet.
  useEffect(() => {
    const handler = async () => {
      if (document.visibilityState === 'visible' && isDashboardVisibleRef.current) {
        // Direct timeUntilSwitch bijwerken zodat tekst meteen klopt
        setTimeUntilSwitch(getTimeUntilNextSwitch());
        try {
          const tab = activeTabRef.current;
          const periodType = tab === "TODAY" ? "day" : "month";
          const r = await apiClient.get("/cases-count-simple-scoreboard", {
            params: { organizationId, period: periodType },
          });
          if (Array.isArray(r.data) && r.data.length > 0 && typeof r.data[0].case_count === "number") {
            const dbScore = r.data[0].case_count;
            if (tab === "TODAY") scoreTodayRef.current = dbScore;
            else scoreMonthRef.current = dbScore;
            setScore(dbScore);
            // Flag: volgende fetchData(false) toont 0 new orders per portal
            forceZeroDeltaRef.current = true;
            // Ook pending counters/recentCases direct toepassen
            if (pendingCountersRef.current) {
              setCounters(pendingCountersRef.current);
              pendingCountersRef.current = null;
            }
            if (pendingRecentCasesRef.current) {
              setRecentCases(pendingRecentCasesRef.current);
              pendingRecentCasesRef.current = null;
            }
          }
        } catch (e) { console.error("[VISIBILITY] Score fetch fout:", e); }

        // Na 10s nieuwe cycle met verse data
        scheduleNextFetch();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [organizationId, setScore, scheduleNextFetch]);

  const ANIMATION_DURATION = 1200;
  const toggleDashboardVisibility = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsDashboardVisible(prev => !prev);
    setIsAnimationEnabled(prev => !prev);
    setTimeout(() => {
      setIsAnimating(false);
      setIsVisible(prev => !prev);
      setHighlightActive(prev => !prev);
    }, ANIMATION_DURATION);
  }, [isAnimating]);

  useEffect(() => {
    const update = () => {
      if (dashboardContainerRef.current && toggleButtonRef.current) {
        const r = dashboardContainerRef.current.getBoundingClientRect();
        toggleButtonRef.current.style.top = isDashboardVisible ? `${r.height + 16}px` : "16px";
      }
    };
    if (isDashboardVisible) {
      setShouldFadeIn(false);
      setShouldFadeInToggle(false);
      const t = setTimeout(() => { setShouldFadeIn(true); update(); setTimeout(() => setShouldFadeInToggle(true), 50); }, 200);
      return () => clearTimeout(t);
    }
    // Bij verbergen de fade-in classes pas weghalen als slideUp (0.6s) klaar is;
    // anders valt de inhoud direct op opacity 0 en schuift er een lege witte kaart weg
    const hideTimer = setTimeout(() => setShouldFadeIn(false), 600);
    update();
    setShouldFadeInToggle(true);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update);
    return () => { clearTimeout(hideTimer); window.removeEventListener("resize", update); window.removeEventListener("scroll", update); };
  }, [isDashboardVisible]);

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape' && !isDashboardVisible) {
        toggleDashboardVisibility();
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDashboardVisible, toggleDashboardVisibility]);

  // Browser-terug in de inbox → dashboard (i.p.v. terug naar /login). Bij
  // openen van de inbox een eigen history-stap op dezelfde url; gaat het
  // dashboard via de knop/Escape weer open, dan die stap weer weghalen.
  const inboxEntryRef = useRef(false);
  useEffect(() => {
    if (!isDashboardVisible && !inboxEntryRef.current) {
      inboxEntryRef.current = true;
      window.history.pushState({ ...window.history.state, inbox: true }, "");
    } else if (isDashboardVisible && inboxEntryRef.current) {
      inboxEntryRef.current = false;
      window.history.back();
    }
  }, [isDashboardVisible]);
  useEffect(() => {
    const onPop = () => {
      // Nog op de inbox-stap (bv. alleen de drawer-stap eraf): niets doen
      if (!inboxEntryRef.current || window.history.state?.inbox) return;
      if (isAnimating) {
        // Toggle loopt nog; stap terugzetten zodat terug later wel werkt
        window.history.pushState({ ...window.history.state, inbox: true }, "");
        return;
      }
      inboxEntryRef.current = false;
      toggleDashboardVisibility();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isAnimating, toggleDashboardVisibility]);

  const [showManagePortals, setShowManagePortals] = useState(false);
  const handleManagePortalsClick = () => {
    setHighlightActive(false);
    setIsVisible(false);
    setShowManagePortals(true);
  };

  const [showSimpleScoreboard, setShowSimpleScoreboard] = useState(false);
  const [scorePollSignal, setScorePollSignal] = useState(0);
  useEffect(() => {
    let t;
    if (!isDashboardVisible) { t = setTimeout(() => setShowSimpleScoreboard(true), 500); }
    else { setShowSimpleScoreboard(false); }
    return () => clearTimeout(t);
  }, [isDashboardVisible]);

  const [isScoreLoading, setIsScoreLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // ── Simple scoreboard polling (dashboard hidden) ──────────────
  // Hoofdcycle draait niet als dashboard hidden is, dus we pollen
  // hier de score. We schrijven direct naar de juiste tab state
  // via setScoreToday/setScoreMonth om race conditions te vermijden.
  const fetchScore = useCallback(async () => {
    try {
      checkAndResetForNewDay();
      const tab = activeTabRef.current;
      const periodType = tab === "TODAY" ? "day" : "month";
      const r = await apiClient.get("/cases-count-simple-scoreboard", {
        params: { organizationId, period: periodType },
      });
      if (Array.isArray(r.data) && r.data.length > 0 && typeof r.data[0].case_count === "number") {
        const newCount = r.data[0].case_count;
        const prevCount = tab === "TODAY" ? scoreTodayRef.current : scoreMonthRef.current;
        if (tab === "TODAY") {
          setScoreToday(newCount);
          scoreTodayRef.current = newCount;
        } else {
          setScoreMonth(newCount);
          scoreMonthRef.current = newCount;
        }
        if (newCount > prevCount) {
          setScorePollSignal((s) => s + 1);
        }
      }
    } catch (e) { console.error(e); }
  }, [organizationId, checkAndResetForNewDay]);

  useEffect(() => {
    if (!showSimpleScoreboard) return;
    fetchScore();
    const interval = setInterval(fetchScore, cycleTimeRef.current);
    return () => clearInterval(interval);
  }, [showSimpleScoreboard, fetchScore]);

  return (
    <div className={`dashboard-page${isPhone ? " phone" : ""}`} style={{ "--cycle-time": `${cycleTime}ms` }}>
      <div className="dashboard-container" ref={dashboardContainerRef}></div>
      <div className={`dashboard ${isDashboardVisible ? "show" : "hide"}`} ref={dashboardWrapperRef}>
        <div className={`dashboard-wrapper ${effectiveState === "sleeping" ? "sleeping-mode" : ""}`}>
          <header className={`dashboard-header ${shouldFadeIn ? "fade-in-header" : ""} ${effectiveState === "sleeping" ? "sleeping" : ""}`}>
            <div className="logo-text-container">
              <img src={effectiveState === "sleeping" ? logoLight : logo} alt="Cadmus Logo" className="logo" />
              <h1 className="welcome-text">
                <span className="welcome">{effectiveState === "sleeping" ? "Good night," : "Welcome,"}</span>
                <span className="company-name">{userName}</span>
              </h1>
            </div>
            <div className="account-section">
              <span onClick={handleMenuOpen} style={{ cursor: 'pointer' }}>My Account</span>
              <Menu
                anchorEl={anchorEl} open={open} onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    minWidth: '240px', backgroundColor: 'rgba(255,255,255)', color: '#111',
                    fontFamily: '"DM Sans",sans-serif', borderRadius: '8px', overflow: 'visible',
                    mt: '8px', padding: '2px 0 8px 0',
                    '&:before': {
                      content: '""', display: 'block', position: 'absolute', width: 24, height: 12,
                      bgcolor: 'rgba(255,255,255)', boxShadow: '0px -1px 3px rgba(0,0,0,0.5)', zIndex: 0,
                      top: -7, right: 16, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)'
                    },
                  }
                }}
              >
                {isAdmin && (
                  <MenuItem onClick={() => { handleMenuClose(); navigate("/register"); }}>
                    <AppRegistrationIcon style={{ fontSize: 20, marginRight: 8, color: '#000' }} />
                    Create Account
                  </MenuItem>
                )}
                <MenuItem onClick={() => { handleMenuClose(); setShowAccountSettings(true); }}>
                  <AccountSettingsIcon />
                  Account Settings
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); setTimeout(handleLogout, 0); }}>
                  <LogoutIcon style={{ fontSize: 20, marginRight: 8, color: '#000' }} />
                  Logout
                </MenuItem>
                <Divider sx={{ backgroundColor: 'rgba(0,0,0,0.2)', margin: '4px 4px' }} />
                <Box sx={{ padding: '8px 20px', fontFamily: '"DM Sans",sans-serif', color: '#000', opacity: 0.4, pointerEvents: 'none', fontSize: '14px', lineHeight: '0' }}>
                  <span>Logged in as</span>
                </Box>
                <Box sx={{ padding: '8px 20px', fontFamily: '"DM Sans",sans-serif', color: '#000', pointerEvents: 'none', fontSize: '14px', lineHeight: '0.5' }}>
                  <span>{localStorage.getItem("email") || "User"}</span>
                </Box>
              </Menu>
              <AvatarImage name={localStorage.getItem("avatar")} size={48} alt="User avatar" className="avatar-image" onClick={handleMenuOpen} />
            </div>
          </header>
          {showAccountSettings && (
            <AccountSettings
              onClose={() => setShowAccountSettings(false)}
              isSleeping={effectiveState === "sleeping"}
              videoPaused={demoVideoPaused}
              onVideoPausedChange={onDemoVideoPausedChange}
              highlightPaused={demoHighlightPaused}
              onHighlightPausedChange={onDemoHighlightPausedChange}
              warningsHidden={demoWarningsHidden}
              onWarningsHiddenChange={setDemoWarningsHidden}
            />
          )}
          <div className={`dashboard-content ${effectiveState}`}>
            <div className={`left-panel ${shouldFadeIn ? "fade-in-left" : ""}`} ref={leftPanelRef}>
              {getBackground() && (
                // Fade via CSS i.p.v. framer-motion: framer liet de afbeelding aan het eind
                // van de fade 1 frame op opacity 0 staan (witte flits, zichtbaar bij 0 portals)
                <img
                  key={getBackground().src}
                  src={getBackground().src}
                  srcSet={getBackground().srcset}
                  alt="Left Panel Background"
                  className="left-panel-image"
                  draggable={false}
                  onContextMenu={e => e.preventDefault()}
                  onDragStart={e => e.preventDefault()}
                />
              )}
              <Fade in={isVisible} timeout={500}>
                <div className="status-box">
                  <img src={getStatusIcon()} alt="Icon" className="status-icon" />
                  <span className="status-text">{getStatusText()}</span>
                </div>
              </Fade>

              {effectiveState === "sleeping" && (
                <div className="sleeping-content">
                  <div className="sleeping-title">
                    Cadmus is sleeping<LoadingDots portalName="sleeping" />
                  </div>
                  <div className="sleeping-subtitle">Check back in {timeUntilSwitch}.</div>
                </div>
              )}

              {portalActivityVisible && (
                <Fade in={isVisible} timeout={500}>
                  <div className="portal-activity" style={{ visibility: effectiveState === "sleeping" ? "hidden" : "visible" }}>
                    <PortalActivity
                      portals={selectedPortals}
                      initialInterval={PORTAL_INTERVAL_MS}
                      startCycle={startCycle}
                      onPortalChange={handlePortalChange}
                    />
                  </div>
                </Fade>
              )}
              <div className="manage-portals-button">
                <ButtonGlass onClick={handleManagePortalsClick} icon={effectiveState === "sleeping" ? managePortalsIcon : managePortalsIcon}>
                  {effectiveState === "sleeping" ? "View Portals" : "Manage Portals"}
                </ButtonGlass>
              </div>
            </div>

            {effectiveState !== "critical" && (
              <div className={`right-panel ${shouldFadeIn ? "fade-in-right" : ""}`}>
                <div className="activity-title">Activity</div>
                <div className="tab-buttons">
                  <button className={`tab-button ${activeTab === "TODAY" ? "active" : ""}`} onClick={() => setActiveTab("TODAY")}>TODAY</button>
                  <button className={`tab-button ${activeTab === "THIS MONTH" ? "active" : ""}`} onClick={() => setActiveTab("THIS MONTH")}>THIS MONTH</button>
                </div>
                <div className="scoreboard">
                  <AnimatedScoreboard currentScore={score} dashboardSate={effectiveState} showLogo={showLogo} />
                </div>
                <span className="top-scanners-text">Top Portals</span>
                <BrandCounter counters={counters} isSleeping={effectiveState === "sleeping"} />
                <div style={{ position: "absolute", width: "100%", height: "100vh" }}>
                  <ReceivedComponent className="last-activity-panel" orders={recentCases} isSleeping={effectiveState === "sleeping"} />
                </div>
                <div className="right-panel-rectangle"></div>
              </div>
            )}

            <AnimatePresence mode="wait">
              {highlightActive && (
                <motion.div
                  key="highlight"
                  className={`highlight ${highlightActive && portalActivityVisible && isAnimationEnabled && !demoHighlightPaused ? "animate" : ""}`}
                  style={{ top: highlightPosition.top, left: highlightPosition.left }}
                  // 0.6 = beginwaarde van @keyframes moveHighlight; anders springt de
                  // opacity van 1 naar 0.6 zodra de CSS animatie het overneemt (flikkering)
                  initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div>
        {!isDashboardVisible && (
          <div className={[effectiveState !== "ok" && effectiveState !== "sleeping" && "red-theme", searchModeActive && "search-theme", isFilterMode && "filter-active-theme"].filter(Boolean).join(" ")}>
            <div className="highlight-datagrid-dynamic" />
            <div className="highlight-datagrid-dynamic-2" />
          </div>
        )}
        <TransparentDataGrid isDashboardVisible={isDashboardVisible} isMultiCompany={isMultiCompany} onSearchModeChange={setIsSearchMode} onFilterModeChange={setIsFilterMode} onNewRows={showSimpleScoreboard ? fetchScore : undefined} pollSignal={scorePollSignal} />
      </div>

      <AnimatePresence>
        {showManagePortals && (
          <motion.div
            className="manage-portals-container"
            initial={{ width: position.width, height: 540, x: position.left, y: position.top, borderRadius: "24px", scale: 1 }}
            animate={{ width: "100vw", height: "100vh", x: 0, y: 0, borderRadius: "0px", scale: 1 }}
            exit={{ width: position.width, height: 540, x: position.left, y: position.top, borderRadius: "24px", scale: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 40, mass: 1, duration: 0.6 }}
            style={{ transformOrigin: "top left", zIndex: 1001 }}
          >
            <ManagePortals backgroundImages={getBackground()} isSleeping={effectiveState === "sleeping"} onBack={() => {
              setShowManagePortals(false);
              setTimeout(() => { setHighlightActive(true); setIsVisible(true); }, 1000);
            }} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="left-panel-highlight-overlay"></div>
      <style>{dynamicKeyframes}</style>

      <AnimatePresence mode="wait">
        {/* Telefoon: geen klok (paste niet, Go to Inbox staat daar nu) */}
        {!searchModeActive && !isPhone && (isMobile && isDashboardVisible ? (
          <motion.div key="digital-clock-mobile-dashboard"
            style={{ position: "absolute", left: 16, bottom: 16, width: "220px", zIndex: 3, pointerEvents: "none" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <DigitalClock />
          </motion.div>
        ) : (
          <motion.div key="digital-clock"
            style={{ position: "absolute", left: isMobile ? 16 : position.left - 38, width: isMobile ? "220px" : "300px", zIndex: 3, pointerEvents: "none" }}
            initial={{ top: 16, opacity: 0 }}
            animate={{ top: isDashboardVisible ? 740 : 16, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "circInOut" }}
          >
            <DigitalClock />
          </motion.div>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {isDashboardVisible && (
          <motion.div
            variants={inboxButtonVariants} initial="hidden" animate={shouldFadeIn ? "visible" : "hidden"} exit="exit"
            onUpdate={noWaapi}
            onClick={toggleDashboardVisibility}
            style={isPhone
              // Telefoon: linksonder, waar op desktop-mobiel de klok stond
              ? { position: "fixed", left: 16, bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)", display: "flex", flexDirection: "row-reverse", alignItems: "center", gap: 12, zIndex: 3, cursor: "pointer" }
              : { position: "absolute", right: window.innerWidth - dashboardRight + 16, top: 760, display: "flex", alignItems: "center", gap: 12, zIndex: 3, cursor: "pointer" }}
          >
            <span style={{ color: "#FFF", textAlign: "right", fontFamily: '"DM Sans"', fontSize: 14, fontWeight: 500, lineHeight: "16px" }}>
              Go to Inbox
            </span>
            <div
              style={{ width: 48, height: 48, borderRadius: 100, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.10)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.2s ease" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.20)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.10)"}
            >
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3v10M3.5 8.5l4.5 4.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`dashboard-toggle-section ${shouldFadeInToggle ? "fade-in-toggle" : ""}${isPhone ? " phone" : ""}`} ref={toggleButtonRef}>
        {/* Telefoon: geen Hide Dashboard (Go to Inbox doet dat) */}
        {!(isPhone && isDashboardVisible) && <button className="dashboard-toggle-button" onClick={toggleDashboardVisibility}>
          {isPhone ? (
            // Telefoon: zelfde knop als Go to Inbox (rond vlak 48px, pijl), maar omhoog
            <>
              <span className="dashboard-toggle-circle">
                <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="dashboard-toggle-label">Go to Dashboard</span>
            </>
          ) : (
            <>
              <img src={isDashboardVisible ? collapseIcon : expandIcon} alt={isDashboardVisible ? "Collapse Icon" : "Expand Icon"} className="dashboard-toggle-icon" />
              <span>{isDashboardVisible ? "Hide Dashboard" : "Show Dashboard"}</span>
            </>
          )}
        </button>}
        <AnimatePresence mode="wait">
          {showSimpleScoreboard && !searchModeActive && (
            <motion.div key="simple-scoreboard" className="simple-scoreboard"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
              onUpdate={noWaapi}
            >
              <SimpleScoreboard currentScore={score} periodType={activeTabRef.current} compact={isPhone} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// AppLayout Component
function AppLayout() {
  const [isDashboardVisible, setIsDashboardVisible] = useState(true);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchModeActive = !isDashboardVisible && isSearchMode;
  // ── Presentation mode — pauzeert achtergrondvideo/glow-animatie handmatig ──
  // (bv. tijdens een Microsoft Teams demo, waar continue schermwijzigingen
  // het delen traag maken). Staat hier omdat VideoBackground buiten
  // DashboardScreen valt; de toggle zelf leeft in Account Settings.
  const [demoVideoPaused, setDemoVideoPaused] = useState(false);
  const [demoHighlightPaused, setDemoHighlightPaused] = useState(false);
  return (
    <div className="app-container">
      <VideoBackground filtered={searchModeActive} paused={demoVideoPaused} />
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/register" element={<RegisterScreen />} />
        <Route path="/register/lab-info" element={<LabInfoScreen />} />
        <Route path="/register/pricing" element={<PricingScreen />} />
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardScreen
              onDashboardVisibleChange={setIsDashboardVisible}
              onSearchModeChange={setIsSearchMode}
              searchModeActive={searchModeActive}
              demoVideoPaused={demoVideoPaused}
              onDemoVideoPausedChange={setDemoVideoPaused}
              demoHighlightPaused={demoHighlightPaused}
              onDemoHighlightPausedChange={setDemoHighlightPaused}
            />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
}

// App Component
function App() {
  return (
    <Router>
      <RegisterProvider>
        <AppLayout />
      </RegisterProvider>
    </Router>
  );
}

export default App;